import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios, { API } from '@/utils/api';

const VoiceRecorder = ({ onTranscription, disabled = false, onLimitReached }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      streamRef.current = stream;

      // Determine best supported format
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Process the recording
        const audioBlob = new Blob(chunksRef.current, { type: mimeType });
        await sendToTranscription(audioBlob);
      };

      // Start recording
      mediaRecorder.start(100); // Collect data every 100ms
      setIsRecording(true);
      setRecordingTime(0);

      // Track voice button click for analytics (use axios for CSRF)
      try {
        await axios.post(`${API}/track/voice-click`);
      } catch (e) {
        // Tracking isn't critical - log and continue
        console.debug('Voice tracking skipped:', e.message);
      }

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Microphone access denied. Please allow microphone access to use voice typing.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No microphone found. Please connect a microphone.');
      } else {
        toast.error('Failed to start recording. Please try again.');
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const sendToTranscription = async (audioBlob) => {
    setIsProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      // Use axios for proper CSRF token handling
      const response = await axios.post(`${API}/transcribe`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      
      if (data.text && data.text.trim()) {
        onTranscription(data.text.trim());
        toast.success('Voice transcribed successfully!');
        
        // Mark voice usage for badge
        try {
          await axios.post(`${API}/user/voice-used`);
        } catch (e) {
          // Badge tracking isn't critical
          console.debug('Voice usage tracking skipped:', e.message);
        }
      } else {
        toast.info('No speech detected. Please try again.');
      }

    } catch (error) {
      console.error('Transcription error:', error);
      
      // Check for limit reached (403 Forbidden) - handle both CSRF and feature limit
      if (error.response?.status === 403) {
        const errorDetail = error.response?.data?.detail || '';
        
        // If it's a voice typing limit error (not CSRF), show upgrade prompt
        if (errorDetail.includes('Voice typing limit') || errorDetail.includes('limit reached')) {
          if (onLimitReached) {
            onLimitReached('voice_typing', errorDetail);
          }
          return;
        }
        // CSRF errors are auto-retried by axios interceptor, so if we get here
        // it means the retry also failed - show a user-friendly message
        if (errorDetail.includes('CSRF')) {
          toast.error('Session expired. Please refresh the page and try again.');
          return;
        }
      }
      
      // Provide more helpful error messages
      let userMessage = error.response?.data?.detail || error.message || 'Failed to transcribe audio';
      
      if (userMessage.includes('disturbed') || userMessage.includes('locked')) {
        userMessage = 'Voice recording failed. Please refresh the page and try again.';
      } else if (userMessage.includes('network') || userMessage.includes('fetch') || error.code === 'ERR_NETWORK') {
        userMessage = 'Network error. Please check your connection and try again.';
      }
      
      toast.error(userMessage);
    } finally {
      setIsProcessing(false);
      setRecordingTime(0);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClick = () => {
    if (disabled || isProcessing) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isProcessing}
        className={`
          relative p-2 rounded-full transition-all duration-300
          ${isRecording 
            ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
            : 'hover:opacity-90'
          }
          ${(disabled || isProcessing) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] focus:ring-offset-2
        `}
        style={!isRecording ? { background: 'var(--theme-button-bg)' } : undefined}
        title={isRecording ? 'Stop recording' : 'Start voice typing'}
        data-testid="voice-recorder-btn"
      >
        {isProcessing ? (
          <Loader2 className="w-5 h-5 text-white animate-spin" />
        ) : isRecording ? (
          <MicOff className="w-5 h-5 text-white" />
        ) : (
          <Mic className="w-5 h-5 text-white" />
        )}
        
        {/* Recording indicator */}
        {isRecording && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-400 rounded-full animate-ping" />
        )}
      </button>

      {/* Recording time display */}
      {(isRecording || isProcessing) && (
        <span className={`ml-2 text-sm font-mono ${isRecording ? 'text-red-400' : 'text-[var(--theme-primary)]'}`}>
          {isProcessing ? 'Processing...' : formatTime(recordingTime)}
        </span>
      )}
    </div>
  );
};

export default VoiceRecorder;
